import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Intervention, PlannedWeekData, Room, Weekday } from '../../../types/weekOverview';
import {
  CALENDAR_END_HOUR,
  CALENDAR_HOUR_HEIGHT,
  CALENDAR_PRINT_HOUR_HEIGHT,
  CALENDAR_START_HOUR,
  CALENDAR_VERTICAL_PADDING,
  WEEKDAYS_ORDER,
  WEEKDAY_FULL_TRANSLATION_KEYS
} from '../../../constants/weekOverviewConstants';
import { getDateForWeekday, formatShortDate } from '../../../utils/weekDateUtils';
import { layoutEventsForDay } from '../../../utils/calendarLayoutUtils';
import { InterventionCard } from './InterventionCard';
import styles from './WeekCalendar.module.css';

interface WeekCalendarProps {
  // Planned weeks only: the calendar reads closedDays/rooms/staff, which the
  // unplanned shell doesn't carry — callers narrow on isPlanned first.
  weekData: PlannedWeekData;
  filteredInterventions: Intervention[];
  selectedInterventionId?: string | null;
  printMode?: boolean;
  onInterventionClick: (intervention: Intervention, anchorRect: DOMRect) => void;
}

// Nudges each hour label up so it sits centred on its grid line.
const TIME_LABEL_OFFSET_PX = 6;

export const WeekCalendar = ({
  weekData,
  filteredInterventions,
  selectedInterventionId = null,
  printMode = false,
  onInterventionClick
}: WeekCalendarProps) => {
  const { t, i18n } = useTranslation();

  const hourHeight = printMode ? CALENDAR_PRINT_HOUR_HEIGHT : CALENDAR_HOUR_HEIGHT;

  const hours = useMemo(() => {
    const list: number[] = [];
    for (let h = CALENDAR_START_HOUR; h <= CALENDAR_END_HOUR; h++) list.push(h);
    return list;
  }, []);

  const totalHours = CALENDAR_END_HOUR - CALENDAR_START_HOUR;
  const columnHeight = totalHours * hourHeight + CALENDAR_VERTICAL_PADDING * 2;

  const laidOutByDay = useMemo(() => {
    const grouped = new Map<Weekday, Intervention[]>();
    for (const day of WEEKDAYS_ORDER) grouped.set(day, []);
    for (const i of filteredInterventions) {
      const list = grouped.get(i.day);
      if (list) list.push(i);
    }
    const result = new Map<Weekday, ReturnType<typeof layoutEventsForDay<Intervention>>>();
    for (const [day, list] of grouped) {
      result.set(day, layoutEventsForDay(list));
    }
    return result;
  }, [filteredInterventions]);

  const roomsById = useMemo(() => {
    const map = new Map<string, Room>();
    for (const r of weekData.rooms) map.set(r.id, r);
    return map;
  }, [weekData.rooms]);

  const staffById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of weekData.staff) map.set(s.id, s.name);
    return map;
  }, [weekData.staff]);

  return (
    <div className={styles.calendarContainer}>
      <div className={styles.scrollWrapper}>
        <div className={styles.calendarGrid}>
          <div className={styles.timeAxisHeader} />
          {WEEKDAYS_ORDER.map((day) => {
            const date = getDateForWeekday(weekData.year, weekData.weekNumber, day);
            return (
              <div key={day} className={styles.dayHeader}>
                {t(WEEKDAY_FULL_TRANSLATION_KEYS[day])}
                <span className={styles.dayHeaderDate}>{formatShortDate(date, i18n.language)}</span>
              </div>
            );
          })}

          <div className={styles.timeLabel} style={{ height: columnHeight }}>
            {hours.map((hour, idx) => (
              <div
                key={hour}
                className={styles.timeLabelHour}
                style={{ top: idx * hourHeight - TIME_LABEL_OFFSET_PX + CALENDAR_VERTICAL_PADDING }}
              >
                {String(hour).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {WEEKDAYS_ORDER.map((day) => {
            const isClosed = weekData.closedDays.includes(day);
            const laidOut = laidOutByDay.get(day) ?? [];
            return (
              <div
                key={day}
                className={`${styles.dayColumn} ${isClosed ? styles.dayColumnClosed : ''}`}
                style={{ height: columnHeight }}
              >
                {hours.map((hour, idx) =>
                  idx === 0 ? null : (
                    <div
                      key={`hour-line-${hour}`}
                      className={styles.hourLine}
                      style={{ top: idx * hourHeight + CALENDAR_VERTICAL_PADDING }}
                    />
                  )
                )}
                {hours.map((hour, idx) =>
                  idx === hours.length - 1 ? null : (
                    <div
                      key={`half-hour-line-${hour}`}
                      className={styles.halfHourLine}
                      style={{
                        top: idx * hourHeight + hourHeight / 2 + CALENDAR_VERTICAL_PADDING
                      }}
                    />
                  )
                )}
                {isClosed ? (
                  <span className={styles.closedLabel}>{t('week-overview-calendar-closed')}</span>
                ) : (
                  laidOut.map(({ event, leftFraction, widthFraction }) => {
                    const staffName = event.staffIds[0]
                      ? staffById.get(event.staffIds[0])
                      : undefined;
                    return (
                      <InterventionCard
                        key={event.id}
                        intervention={event}
                        room={roomsById.get(event.roomId)}
                        staffName={staffName}
                        leftFraction={leftFraction}
                        widthFraction={widthFraction}
                        hourHeight={hourHeight}
                        isSelected={event.id === selectedInterventionId}
                        onClick={onInterventionClick}
                      />
                    );
                  })
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
